/**
 * Redactarea DETERMINISTĂ a datelor sensibile din textul vizitatorului
 * (31 aug 2026, cerere Radu): numere de card, IBAN, CNP, email, secrete
 * dictate („parola mea e...", „pin 1234"). Fără LLM, prin construcție - rostul
 * ei e tocmai ca datele astea să NU ajungă la model, în istoricul stocat sau
 * în dashboardul clientului.
 *
 * Rulează în DOUĂ straturi, cu aceeași funcție:
 *  - în SDK, înainte ca mesajul să plece din browser (stratul ideal: datele nu
 *    ating nici măcar serverul nostru);
 *  - pe server, la intrarea în orchestrator (granița de neocolit: SDK vechi,
 *    apeluri directe pe API, transcriptul de voce).
 *
 * Falsele pozitive sunt dușmanul principal - un magazin e plin de cifre
 * (prețuri, EAN-uri, id-uri de variantă, numere de comandă). De-aia fiecare
 * detector cere o VALIDARE matematică, nu doar o formă:
 *  - card: prefix de schemă cunoscută + lungimea schemei + suma Luhn;
 *  - IBAN: structura + mod-97 == 1;
 *  - CNP: 13 cifre + lună/zi valide + cifra de control;
 *  - email: forma standard (aici forma chiar e suficientă);
 *  - secret: doar lipit de un cuvânt-cheie („parola", „pin", „cvv").
 *
 * Placeholderele sunt scurte, neutre de limbă și AUTODESCRIPTIVE - le văd și
 * vizitatorul (în propria bulă) și modelul (care primește și o notă separată
 * în prompt, vezi promptBuilder).
 */

export type SensitiveKind = "card" | "iban" | "cnp" | "email" | "secret";

const PLACEHOLDER: Record<SensitiveKind, string> = {
  card: "[card ••••]",
  iban: "[IBAN ••••]",
  cnp: "[CNP ••••]",
  email: "[email ••••]",
  secret: "[secret ••••]",
};

/** Suma Luhn - standardul cardurilor; un șir aleator de cifre trece ~10%. */
function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * Prefix + lungime pe schemele reale. Aici cade grosul falselor pozitive:
 * un id de variantă Shopify are 14 cifre și începe cu 4 (Visa cere 13/16/19),
 * un EAN are 13 și începe adesea cu 5 (Mastercard cere 16).
 */
function cardSchemeValid(digits: string): boolean {
  const len = digits.length;
  const p2 = Number(digits.slice(0, 2));
  const p4 = Number(digits.slice(0, 4));
  if (digits[0] === "4") return len === 13 || len === 16 || len === 19;
  if ((p2 >= 51 && p2 <= 55) || (p4 >= 2221 && p4 <= 2720)) return len === 16;
  if (p2 === 34 || p2 === 37) return len === 15;
  if (p4 === 6011 || p2 === 65 || (Number(digits.slice(0, 3)) >= 644 && Number(digits.slice(0, 3)) <= 649))
    return len >= 16 && len <= 19;
  if (p4 >= 3528 && p4 <= 3589) return len >= 16 && len <= 19;
  if ((p2 >= 30 && Number(digits.slice(0, 3)) <= 305) || p2 === 36 || p2 === 38) return len >= 14 && len <= 16;
  return false;
}

/** mod-97 pe IBAN (ISO 13616): primele 4 mutate la coadă, litere → numere. */
function ibanValid(candidate: string): boolean {
  if (candidate.length < 15 || candidate.length > 34) return false;
  const rearranged = candidate.slice(4) + candidate.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    const value = code >= 65 ? String(code - 55) : ch;
    for (const digit of value) remainder = (remainder * 10 + (digit.charCodeAt(0) - 48)) % 97;
  }
  return remainder === 1;
}

/** CNP: S AA LL ZZ JJ NNN C - lună/zi plauzibile + cifra de control. */
function cnpValid(digits: string): boolean {
  if (digits.length !== 13) return false;
  const month = Number(digits.slice(3, 5));
  const day = Number(digits.slice(5, 7));
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const weights = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
  let sum = 0;
  for (let i = 0; i < 12; i++) sum += (digits.charCodeAt(i) - 48) * weights[i]!;
  let control = sum % 11;
  if (control === 10) control = 1;
  return control === digits.charCodeAt(12) - 48;
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Grupuri de cifre cu separatoare uzuale (spații, liniuțe, puncte) - candidați de card. */
const DIGIT_RUN_RE = /(?<![\d])(?:\d[ .\-]?){12,18}\d(?![\d])/g;

/** IBAN, eventual cu spații de grupare așa cum îl copiază oamenii. */
const IBAN_RE = /(?<![A-Za-z0-9])[A-Za-z]{2}\d{2}(?:[ ]?[A-Za-z0-9]{2,4}){3,8}(?![A-Za-z0-9])/g;

/**
 * Secretele dictate: cuvântul-cheie + eventuali conectori („mea", „e", „:")
 * + tokenul de redactat. Doar tokenul cade - restul frazei rămâne. Pragul de
 * 3 caractere lasă în pace negațiile („parola nu merge").
 */
const SECRET_RE =
  /\b(parol[aă]\w*|password|passcode|pin(?:-?ul)?|cvv2?|cvc|cod(?:ul)?\s+(?:pin|de\s+securitate|cvv))((?:\s+(?:meu|mea|e|este|era))*\s*[:=]?\s+|\s*[:=]\s*)(\S{3,})/gi;

/** Cuvinte care după un cuvânt-cheie NU sunt secrete („parola nu merge", „pinul este greșit"). */
const SECRET_STOPWORDS = new Set([
  "nu", "merge", "gresit", "greșit", "gresita", "greșită", "uitat", "uitata", "uitată",
  "pierdut", "pierduta", "pierdută", "schimb", "schimbat", "resetez", "resetat",
  "expirat", "expirata", "expirată", "card", "cont", "veche", "vechea", "noua", "nouă",
  "valabila", "valabilă", "corecta", "corectă", "buna", "bună", "mea", "meu",
  "the", "not", "wrong", "forgot", "reset", "doesn't", "invalid",
]);

export interface RedactionResult {
  text: string;
  /** Felurile găsite, deduplicate, în ordinea primei apariții. */
  kinds: SensitiveKind[];
}

export function redactSensitive(input: string): RedactionResult {
  const kinds: SensitiveKind[] = [];
  const hit = (kind: SensitiveKind): string => {
    if (!kinds.includes(kind)) kinds.push(kind);
    return PLACEHOLDER[kind];
  };
  let text = input;

  text = text.replace(EMAIL_RE, () => hit("email"));

  text = text.replace(IBAN_RE, (match) => {
    const compact = match.replace(/[ ]/g, "").toUpperCase();
    return ibanValid(compact) ? hit("iban") : match;
  });

  text = text.replace(DIGIT_RUN_RE, (match) => {
    const digits = match.replace(/[ .\-]/g, "");
    if (digits.length === 13 && cnpValid(digits)) return hit("cnp");
    if (digits.length >= 13 && digits.length <= 19 && cardSchemeValid(digits) && luhnValid(digits))
      return hit("card");
    return match;
  });

  text = text.replace(SECRET_RE, (match, keyword: string, connector: string, token: string) => {
    if (SECRET_STOPWORDS.has(token.toLowerCase())) return match;
    return `${keyword}${connector}${hit("secret")}`;
  });

  return { text, kinds };
}
